import React from 'react';
import { useTranslation } from 'react-i18next';
import useRevealOnScroll from '../../hooks/useRevealOnScroll';
import BackButton from '../../components/BackButton';

import Avigilon from '../../images/video/avigilon.jpg';
import Wisenet from '../../images/video/wisenet.jpg';
import Milestone from '../../images/video/milestone.png';
import Recfaces from '../../images/video/recfaces.png';
import Pelco from '../../images/video/pelco.png';
import Hik from '../../images/video/hik.jpg';

const tints = ['0,194,255', '0,112,210', '163,230,53'];

function SolutionRow({ idx, item, reversed, tint }) {
  const [revealRef, isVisible] = useRevealOnScroll(0.1);

  const handleSpotlight = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty('--x', `${((e.clientX - rect.left) / rect.width) * 100}%`);
    e.currentTarget.style.setProperty('--y', `${((e.clientY - rect.top) / rect.height) * 100}%`);
  };

  return (
    <div
      ref={revealRef}
      className={`reveal group flex flex-col md:items-center gap-8 md:gap-16 py-14 sm:py-16 ${
        reversed ? 'md:flex-row-reverse' : 'md:flex-row'
      } ${isVisible ? 'is-visible' : ''}`}
    >
      <div className="flex-1 min-w-0">
        <span
          className="font-display font-extrabold text-white/[0.04] block leading-none select-none text-[96px] sm:text-[120px] -mb-8 sm:-mb-10"
          aria-hidden="true"
        >
          {String(idx + 1).padStart(2, '0')}
        </span>
        <h3 className="font-display text-2xl sm:text-3xl font-bold text-white mb-4 relative">
          {item.name}
        </h3>
        <p className="text-white/60 leading-relaxed max-w-lg relative">
          {item.description}
        </p>
      </div>

      <div
        onMouseMove={handleSpotlight}
        className="spotlight glass-card flex-none w-full md:w-[380px] h-[220px] sm:h-[260px] rounded-3xl relative overflow-hidden transition-colors duration-300 group-hover:border-cyan-400/40"
      >
        <img
          src={item.image}
          alt={item.name}
          className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-500"
        />
        <div
          className="absolute inset-0"
          style={{
            background: `radial-gradient(circle at ${reversed ? '70%' : '30%'} 25%, rgba(${tint},0.25), transparent 60%), linear-gradient(180deg, rgba(5,8,16,0.05), rgba(5,8,16,0.55))`,
          }}
        />
      </div>
    </div>
  );
}

export default function VideoSolutions() {
  const { t } = useTranslation('videosolutions');
  const [headerRef, headerVisible] = useRevealOnScroll(0.3);

  const solutions = [
    { name: 'Pelco', description: t('solutions.pelco'), image: Pelco },
    { name: 'Hikvision', description: t('solutions.hikvision'), image: Hik },
    { name: 'Motorola Solutions', description: t('solutions.motorola'), image: Avigilon },
    { name: 'Wisenet', description: t('solutions.wisenet'), image: Wisenet },
    { name: 'Milestone Systems', description: t('solutions.milestone'), image: Milestone },
    { name: 'RecFaces', description: t('solutions.recfaces'), image: Recfaces },
  ];

  return (
    <section className="relative min-h-screen text-white bg-gradient-to-b from-[#070b15] to-[#0a111f] pt-32 pb-24 overflow-hidden">
      <div className="grid-bg opacity-60" />
      <div className="glow-orb w-[560px] h-[560px] bg-[#0070d2] opacity-20 -top-40 -left-32 [animation:drift-slow_24s_ease-in-out_infinite,glow-pulse_9s_ease-in-out_infinite]" />
      <div className="glow-orb w-[480px] h-[480px] bg-[#00c2ff] opacity-20 top-1/3 right-[-160px] [animation:drift_15s_ease-in-out_infinite_2s,glow-pulse_9s_ease-in-out_infinite_1.5s]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <BackButton label={t('back')} to="/" hash="#services" />

        <div ref={headerRef} className={`reveal max-w-2xl ${headerVisible ? 'is-visible' : ''}`}>
          <p className="font-mono text-xs tracking-[0.3em] text-white/40 mb-2">
            01&ndash;{String(solutions.length).padStart(2, '0')}
          </p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-white">
            {t('title')}
          </h1>
          <span className={`title-accent ${headerVisible ? 'is-visible' : ''}`} />
          <p className="text-white/60 text-lg mt-6">
            {t('subtitle')}
          </p>
        </div>

        <div className="divide-y divide-white/10 mt-4">
          {solutions.map((item, idx) => (
            <SolutionRow
              key={item.name}
              idx={idx}
              item={item}
              reversed={idx % 2 === 1}
              tint={tints[idx % tints.length]}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
